import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  RefreshTokenResponse,
  AdminUser,
  ChangePasswordRequest
} from '@shared/types/auth';
import { API_ENDPOINTS, STORAGE_KEYS } from '@shared/constants';

class AuthService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // TEMPORARY FIX: Hard-coded backend URL
    this.baseURL = 'https://gmhv2main-production.up.railway.app';
    console.log('🔍 DEBUG: Using hard-coded baseURL =', this.baseURL);
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Include cookies for refresh token
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't try to refresh token for auth endpoints
        if (error.response?.status === 401 && 
            !originalRequest._retry && 
            !originalRequest.url?.includes('/auth/')) {
          
          originalRequest._retry = true;

          try {
            const refreshed = await this.refreshToken();
            if (refreshed) {
              // Retry original request with new token
              const token = this.getAccessToken();
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens but don't redirect if on login page
            this.logout();
            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Login admin user
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await this.api.post(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      const { data } = response.data;
      
      if (data) {
        // Store tokens and user data
        this.setAccessToken(data.token);
        this.setUserData(data.user);
        
        // Refresh token is stored in httpOnly cookie by backend
        console.log('✅ Login successful');
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const response: AxiosResponse<RefreshTokenResponse> = await this.api.post(
        API_ENDPOINTS.AUTH.REFRESH
      );

      const { data } = response.data;
      
      if (data) {
        this.setAccessToken(data.token);
        console.log('✅ Token refreshed successfully');
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('❌ Token refresh error:', error);
      return false;
    }
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    try {
      await this.api.post(API_ENDPOINTS.AUTH.LOGOUT);
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      // Clear local storage regardless of API response
      this.clearAuthData();
      console.log('✅ Logout completed');
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<AdminUser | null> {
    try {
      const response = await this.api.get(API_ENDPOINTS.AUTH.PROFILE);
      return response.data.data.user;
    } catch (error: any) {
      console.error('❌ Get profile error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Change password
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<void> {
    try {
      await this.api.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, passwordData);
      console.log('✅ Password changed successfully');
    } catch (error: any) {
      console.error('❌ Change password error:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Check if token is valid
   */
  async checkAuth(): Promise<boolean> {
    try {
      const token = this.getAccessToken();
      if (!token) return false;

      const response = await this.api.get(API_ENDPOINTS.AUTH.CHECK);
      return response.data.success;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get access token from localStorage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Set access token to localStorage
   */
  private setAccessToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  /**
   * Get user data from localStorage
   */
  getUserData(): AdminUser | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  /**
   * Set user data to localStorage
   */
  private setUserData(user: AdminUser): void {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const userData = this.getUserData();
    return !!(token && userData);
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (error.response?.data?.error) {
      return new Error(error.response.data.error.message || 'Bir hata oluştu');
    }
    
    if (error.message) {
      return new Error(error.message);
    }
    
    return new Error('Bilinmeyen bir hata oluştu');
  }

  /**
   * Get API instance for other services
   */
  getApiInstance(): AxiosInstance {
    return this.api;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
